import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSlaExportStatus,
  triggerSlaExport,
  type SlaExportParams,
} from './exportSlaSummary';

const POLL_INTERVAL_MS = 2000;

interface UseSlaExportReturn {
  isExporting: boolean;
  error: string | null;
  startExport: (params: SlaExportParams) => Promise<void>;
}

export function useSlaExport(): UseSlaExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startExport = useCallback(
    async (params: SlaExportParams) => {
      if (isExporting) return;

      setIsExporting(true);
      setError(null);
      stopPolling();

      try {
        const serviceStatus = params.status || params.serviceStatus;
        const { jobId } = await triggerSlaExport({
          ...params,
          serviceStatus,
        });

        pollingRef.current = setInterval(async () => {
          try {
            const status = await getSlaExportStatus(jobId);

            if (!mountedRef.current) {
              stopPolling();
              return;
            }

            if (status.status === 'completed' && status.signedUrl) {
              stopPolling();
              setIsExporting(false);
              window.open(status.signedUrl, '_blank');
            } else if (status.status === 'failed') {
              stopPolling();
              setIsExporting(false);
              setError(status.error ?? 'Export failed');
            }
          } catch (pollError) {
            stopPolling();
            if (mountedRef.current) {
              setIsExporting(false);
              setError(
                pollError instanceof Error
                  ? pollError.message
                  : 'Failed to check export status'
              );
            }
          }
        }, POLL_INTERVAL_MS);
      } catch (triggerError) {
        if (mountedRef.current) {
          setIsExporting(false);
          setError(
            triggerError instanceof Error
              ? triggerError.message
              : 'Failed to start export'
          );
        }
      }
    },
    [isExporting, stopPolling]
  );

  return { isExporting, error, startExport };
}
